import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FiPlay, FiPause, FiVolume2, FiVolumeX, FiMaximize, FiMinimize, FiClock, FiCalendar } from 'react-icons/fi';
import { utcToChicago } from '../../utils/datetime-comprehensive';
import logger from '../../utils/logger';

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 0 auto;
  background: var(--video-background, #000);
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  aspect-ratio: 16 / 9;
  max-height: 70vh;

  @media (max-width: 768px) {
    border-radius: 0;
    max-height: 40vh;
  }

  @media (max-width: 480px) {
    border-radius: 0;
    max-height: 38vh;
  }

  /* Fullscreen styles */
  &:fullscreen {
    border-radius: 0;
    max-height: none;
    width: 100vw;
    height: 100vh;
    background: var(--video-background, #000);
  }

  &:-webkit-full-screen {
    border-radius: 0;
    max-height: none;
    width: 100vw;
    height: 100vh;
    background: var(--video-background, #000);
  }

  &:-moz-full-screen {
    border-radius: 0;
    max-height: none;
    width: 100vw;
    height: 100vh;
    background: var(--video-background, #000);
  }

  &:-ms-fullscreen {
    border-radius: 0;
    max-height: none;
    width: 100vw;
    height: 100vh;
    background: var(--video-background, #000);
  }

  /* Spinning animation for loading indicator */
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  background: var(--video-background, #000);
`;

const VideoControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  opacity: ${props => props.$show ? 1 : 0};
  transition: opacity 0.3s ease;
  z-index: 1010;

  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

const PlayButton = styled.button`
  background: var(--primary-color);
  border: none;
  border-radius: 0;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-inverse);
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: var(--primary-hover);
  }
  
  svg { width: 22px; height: 22px; }

  @media (max-width: 768px) {
    width: 52px; height: 52px;
    svg { width: 20px; height: 20px; }
  }

  @media (max-width: 480px) {
    width: 48px; height: 48px;
    svg { width: 18px; height: 18px; }
  }
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 6px;
  background: var(--background-secondary);
  opacity: 0.3;
  border-radius: 0;
  position: relative;
  cursor: pointer;

  @media (max-width: 768px) { height: 8px; }
`;

const ProgressFill = styled.div`
  height: 100%;
  background: var(--primary-color);
  border-radius: 0;
  width: ${props => props.$progress}%;
  transition: width 0.1s ease;
`;

const TimeDisplay = styled.div`
  color: var(--text-inverse);
  font-size: 0.9rem;
  font-weight: 500;
  min-width: 100px;
  text-align: center;
  @media (max-width: 480px) { font-size: 0.8rem; min-width: 88px; }
`;

const VolumeButton = styled.button`
  background: none;
  border: none;
  color: var(--text-inverse);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: var(--hover-overlay);
  }
  
  svg { width: 20px; height: 20px; }
  @media (max-width: 480px) { svg { width: 18px; height: 18px; } }
`;

const FullscreenButton = styled.button`
  background: none;
  border: none;
  color: var(--text-inverse);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: var(--hover-overlay);
  }
  
  svg { width: 20px; height: 20px; }
  @media (max-width: 480px) { svg { width: 18px; height: 18px; } }
`;

const SpeedControlWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const SpeedButton = styled.button`
  background: none;
  border: none;
  color: var(--text-inverse);
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  border-radius: 0;
  transition: background-color 0.2s ease;
  font-size: 0.85rem;
  font-weight: 600;
  min-width: 48px;
  
  &:hover {
    background: var(--hover-overlay);
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    min-width: 42px;
    padding: 0.5rem;
  }
`;

const SpeedMenu = styled.div`
  position: absolute;
  left: 0;
  right: auto;
  bottom: calc(100% + 8px);
  top: auto;
  background: rgba(0, 0, 0, 0.95);
  border-radius: 8px 8px 0 0;
  padding: 0.5rem 0;
  min-width: 120px;
  max-width: 140px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1020;
  display: ${props => props.$show ? 'block' : 'none'};

  @media (max-width: 768px) {
    min-width: 120px;
    max-width: 90vw;
    left: 0;
    right: 0;
    border-radius: 8px 8px 0 0;
  }
`;

const SpeedOption = styled.button`
  width: 100%;
  background: ${props => props.$active ? 'var(--primary-color)' : 'transparent'};
  border: none;
  color: var(--text-inverse);
  cursor: pointer;
  padding: 0.625rem 1rem;
  font-size: 0.9rem;
  font-weight: ${props => props.$active ? '600' : '400'};
  text-align: left;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: ${props => props.$active ? 'var(--primary-color)' : 'var(--hover-overlay)'};
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0.5rem 0.875rem;
  }
`;


const VideoInfo = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: var(--background-secondary);
  border-radius: 0;
  min-width: 0;
  @media (max-width: 768px) { padding: 0.75rem; }
`;

const VideoTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: var(--text-primary);
  font-size: 1.2rem;
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
`;

const VideoMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.375rem;
  color: var(--text-muted);
  font-size: 0.9rem;
  
  @media (min-width: 640px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;
  }
  
  @media (max-width: 768px) { 
    font-size: 0.85rem; 
  }
  @media (max-width: 480px) { 
    font-size: 0.8rem; 
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--text-muted);
  background: var(--background-secondary);
  border-radius: 0;
  border: 1px solid var(--border-color);
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--text-inverse);
  text-align: center;
  background: var(--background-overlay);
  padding: 1.5rem;
  border-radius: 0;
  min-width: 280px;
  max-width: 90vw;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);

  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 0;
    min-width: 260px;
    max-width: 85vw;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 0;
    min-width: 240px;
    max-width: 80vw;
    font-size: 0.9rem;
  }

  @media (max-width: 360px) {
    padding: 0.875rem;
    min-width: 220px;
    max-width: 75vw;
    font-size: 0.85rem;
  }
`;

const LoadingHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  font-size: 1.2rem;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.875rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 0.75rem;
  }

  @media (max-width: 360px) {
    font-size: 0.95rem;
    margin-bottom: 0.625rem;
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid var(--primary-color);
  border-radius: 0;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;

  @media (max-width: 768px) {
    width: 18px;
    height: 18px;
    margin-right: 0.45rem;
  }

  @media (max-width: 480px) {
    width: 16px;
    height: 16px;
    margin-right: 0.4rem;
  }

  @media (max-width: 360px) {
    width: 14px;
    height: 14px;
    margin-right: 0.35rem;
  }
`;

const ProgressContainer = styled.div`
  margin-top: 0.5rem;

  @media (max-width: 768px) {
    margin-top: 0.45rem;
  }

  @media (max-width: 480px) {
    margin-top: 0.4rem;
  }

  @media (max-width: 360px) {
    margin-top: 0.35rem;
  }
`;

const LoadingProgressBar = styled.div`
  width: 240px;
  height: 6px;
  background: var(--background-secondary);
  opacity: 0.2;
  border-radius: 0;
  overflow: hidden;
  margin: 0 auto;

  @media (max-width: 768px) {
    width: 220px;
    height: 5px;
  }

  @media (max-width: 480px) {
    width: 200px;
    height: 4px;
  }

  @media (max-width: 360px) {
    width: 180px;
    height: 4px;
  }
`;

const LoadingProgressFill = styled.div`
  width: ${props => props.$progress}%;
  height: 100%;
  background: linear-gradient(90deg, var(--primary-color), var(--primary-hover));
  transition: width 0.5s ease;
  border-radius: 0;
`;

const ProgressText = styled.div`
  font-size: 1rem;
  margin-top: 0.5rem;
  font-weight: 500;
  color: var(--primary-color);

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-top: 0.45rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-top: 0.4rem;
  }

  @media (max-width: 360px) {
    font-size: 0.85rem;
    margin-top: 0.35rem;
  }
`;

const LoadingStage = styled.div`
  font-size: 0.8rem;
  margin-top: 0.25rem;
  opacity: 0.8;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-top: 0.225rem;
  }

  @media (max-width: 480px) {
    font-size: 0.7rem;
    margin-top: 0.2rem;
  }

  @media (max-width: 360px) {
    font-size: 0.65rem;
    margin-top: 0.175rem;
  }
`;

const FileSizeInfo = styled.div`
  font-size: 0.7rem;
  margin-top: 0.25rem;
  opacity: 0.6;

  @media (max-width: 768px) {
    font-size: 0.65rem;
    margin-top: 0.225rem;
  }

  @media (max-width: 480px) {
    font-size: 0.6rem;
    margin-top: 0.2rem;
  }

  @media (max-width: 360px) {
    font-size: 0.55rem;
    margin-top: 0.175rem;
  }
`;

const InitializingText = styled.div`
  font-size: 0.9rem;
  margin-top: 0.5rem;
  opacity: 0.8;

  @media (max-width: 768px) {
    font-size: 0.85rem;
    margin-top: 0.45rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin-top: 0.4rem;
  }

  @media (max-width: 360px) {
    font-size: 0.75rem;
    margin-top: 0.35rem;
  }
`;

function VideoPlayer({ recording, onError, autoPlay = false }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [lastLoggedProgress, setLastLoggedProgress] = useState(0);
  const [loadingStage, setLoadingStage] = useState('initializing');
  const [bufferedMB, setBufferedMB] = useState(0);
  const [totalMB, setTotalMB] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [fullscreenSupported, setFullscreenSupported] = useState(true);
  const [loadingStartTime, setLoadingStartTime] = useState(null);
  const [tookTooLong, setTookTooLong] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const speedMenuRef = useRef(null);
  const speedWrapperRef = useRef(null);
  const watchdogRef = React.useRef(null);
  const readinessPollRef = React.useRef(null);

  const videoRef = React.useRef(null);

  // Convert external video URL to proxy URL to avoid CORS issues
  const getProxyUrl = React.useCallback((url) => {
    if (!url) return url;
    
    // If it's already a proxy URL, return as is
    if (url.includes('/portal/api/video/proxy')) {
      return url;
    }
    
    // If it's a faithinsight.org or CloudFront URL, convert to proxy URL
    if (url.includes('faithinsight.org') || url.includes('cloudfront.net')) {
      const token = localStorage.getItem('faithinsight_token');
      const tokenParam = token ? `&token=${encodeURIComponent(token)}` : '';
      const proxyUrl = `/portal/api/video/proxy?url=${encodeURIComponent(url)}${tokenParam}`;
      return proxyUrl;
    }
    
    // For other URLs, return as is (they might work or might not)
    return url;
  }, []);

  // State for fallback URL
  const [useFallback, setUseFallback] = useState(false);
  const [fallbackAttempted, setFallbackAttempted] = useState(false);
  const [isVideoNotFound, setIsVideoNotFound] = useState(false);

  // Memoize the proxy URL to prevent excessive recalculations
  const proxyUrl = React.useMemo(() => {
    return getProxyUrl(recording?.url);
  }, [recording?.url, getProxyUrl]);

  // Determine which URL to use
  const videoSrc = useFallback ? recording?.url : proxyUrl;

  // Check if video exists before loading
  React.useEffect(() => {
    if (!proxyUrl || !proxyUrl.includes('/portal/api/video/proxy')) {
      setIsVideoNotFound(false);
      return;
    }

    // Check if video exists by making a HEAD request
    const checkVideoExists = async () => {
      try {
        const token = localStorage.getItem('faithinsight_token');
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        const response = await fetch(proxyUrl, {
          method: 'HEAD',
          headers
        });
        if (response.status === 404) {
          setIsVideoNotFound(true);
          setHasError(true);
          setIsLoading(false);
          return;
        }
        // If we get a 401, don't mark as not found - let the video element handle it
        if (response.status === 401) {
          logger.warn('Video HEAD request returned 401 - authentication issue');
          setIsVideoNotFound(false);
          return;
        }
        setIsVideoNotFound(false);
      } catch (error) {
        // If HEAD request fails, we'll let the video element try to load
        // and handle the error there
        setIsVideoNotFound(false);
      }
    };

    checkVideoExists();
  }, [proxyUrl]);

  // Debug logging and mobile-optimized preload
  React.useEffect(() => {
    // Reset fallback state when recording changes
    setUseFallback(false);
    setFallbackAttempted(false);
    setIsVideoNotFound(false);
    
    // Don't try to load if video is not found
    if (isVideoNotFound) {
      return;
    }
    
    // Mobile-optimized loading strategy
    if (videoRef.current && proxyUrl) {
      try {
        // On mobile, use more conservative preload settings
        if (isMobile) {
          videoRef.current.preload = 'metadata';
          // Don't aggressively load on mobile - let user interaction trigger it
        } else {
          videoRef.current.preload = 'auto';
          videoRef.current.load();
        }
        
        // Only try aggressive loading on desktop
        if (!isMobile) {
          // Try to start loading metadata immediately
          if (videoRef.current.readyState < 1) {
            setTimeout(() => {
              if (videoRef.current) {
                videoRef.current.load();
              }
            }, 100);
          }
          
          // For very large files, try to preload the first chunk
          setTimeout(() => {
            if (videoRef.current && videoRef.current.readyState >= 1) {
              // Video has metadata, try to start buffering
              videoRef.current.currentTime = 0;
            }
          }, 500);
        }
      } catch (error) {
        logger.error('Error during video preload setup', { error, recordingId: recording?.id });
      }
    }
  }, [recording, proxyUrl, videoSrc, isMobile, isVideoNotFound]);

  // Auto-play functionality - disabled on mobile to prevent crashes
  React.useEffect(() => {
    if (autoPlay && videoRef.current && !isLoading && duration > 0 && !isMobile) {
      const playVideo = async () => {
        try {
          await videoRef.current.play();
        } catch (error) {
          // Auto-play might fail due to browser policies, which is normal
        }
      };
      playVideo();
    }
  }, [autoPlay, isLoading, duration, isMobile]);

  // Check fullscreen support after video element is available
  React.useEffect(() => {
    if (videoRef.current) {
      const hasFullscreenAPI = !!(
        document.documentElement.requestFullscreen ||
        document.documentElement.webkitRequestFullscreen ||
        document.documentElement.mozRequestFullScreen ||
        document.documentElement.msRequestFullscreen
      );
      
      // For mobile, check multiple fullscreen methods
      const hasVideoFullscreen = !!(videoRef.current.webkitEnterFullscreen);
      const hasContainerFullscreen = !!(videoRef.current.parentElement?.webkitRequestFullscreen);
      
      // On mobile, fullscreen is supported if any method is available OR we can use CSS fallback
      const mobileFullscreenSupported = isMobile ? (hasFullscreenAPI || hasVideoFullscreen || hasContainerFullscreen || true) : (hasFullscreenAPI || hasVideoFullscreen);
      
      setFullscreenSupported(mobileFullscreenSupported);
    }
  }, [proxyUrl, isMobile]);

  // Detect mobile device and fullscreen support
  React.useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsMobile(isMobileDevice || isSmallScreen || isTouchDevice);
    };

    const checkFullscreenSupport = () => {
      const hasFullscreenAPI = !!(
        document.documentElement.requestFullscreen ||
        document.documentElement.webkitRequestFullscreen ||
        document.documentElement.mozRequestFullScreen ||
        document.documentElement.msRequestFullscreen
      );
      
      // For mobile, also check if video element supports fullscreen
      const hasVideoFullscreen = !!(videoRef.current && videoRef.current.webkitEnterFullscreen);
      
      // Set fullscreen support - will be true if either API is available
      setFullscreenSupported(hasFullscreenAPI || hasVideoFullscreen);
    };

    checkMobile();
    checkFullscreenSupport();
    
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Handle fullscreen change events
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    // Add event listeners for all fullscreen change events
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Ensure loading overlay is hidden when video is playing
  React.useEffect(() => {
    if (isPlaying && isLoading) {
      setIsLoading(false);
      setLoadingStage('ready');
    }
  }, [isPlaying, isLoading]);

  // Aggressively hide loading overlay when video is paused
  React.useEffect(() => {
    if (!isPlaying && isLoading) {
      setIsLoading(false);
      setLoadingStage('ready');
      // Clear any active timers when paused
      clearWatchdog();
      if (readinessPollRef.current) {
        clearInterval(readinessPollRef.current);
        readinessPollRef.current = null;
      }
    }
  }, [isPlaying, isLoading]);

  // Cleanup effect to prevent memory leaks
  React.useEffect(() => {
    const currentVideoRef = videoRef.current;
    return () => {
      try {
        clearWatchdog();
        if (currentVideoRef) {
          currentVideoRef.pause();
          currentVideoRef.src = '';
          currentVideoRef.load();
        }
      } catch (error) {
        logger.error('Error during VideoPlayer cleanup', { error });
      }
    };
  }, []);

  const formatTime = (time) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        // On mobile, ensure video is unmuted when user clicks play
        if (isMobile && videoRef.current.muted) {
          videoRef.current.muted = false;
          setIsMuted(false);
        }

        videoRef.current.play().then(() => {
          setIsPlaying(true);
        }).catch((error) => {
          logger.error('Video play failed', { error, isMobile });
          // On mobile, try playing muted if unmuted play fails
          if (isMobile && !videoRef.current.muted) {
            videoRef.current.muted = true;
            setIsMuted(true);
            videoRef.current.play().then(() => {
              setIsPlaying(true);
            }).catch((mutedError) => {
              logger.error('Muted video play also failed', { error: mutedError, isMobile });
            });
          }
        });
      }
    }
  };

  const handleProgressClick = (e) => {
    if (videoRef.current && duration > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const newTime = (clickX / rect.width) * duration;
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeToggle = () => {
    if (videoRef.current) {
      if (isMuted) {
        videoRef.current.volume = volume;
        setIsMuted(false);
      } else {
        videoRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  const handleSpeedChange = (speed) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
      setPlaybackSpeed(speed);
      setShowSpeedMenu(false);
    }
  };

  const handleFullscreen = () => {
    const container = videoRef.current?.parentElement;
    if (!container) return;

    // Check if already in fullscreen
    if (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement || isFullscreen) {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else if (isFullscreen && container) {
        // Exit CSS fullscreen fallback
        container.style.position = '';
        container.style.top = '';
        container.style.left = '';
        container.style.width = '';
        container.style.height = '';
        container.style.zIndex = '';
        container.style.backgroundColor = '';
        setIsFullscreen(false);
      }
    } else {
      // Enter fullscreen - try different methods for better mobile support
      try {
        if (container.requestFullscreen) {
          container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) {
          // iOS Safari - try with Element.ALLOW_KEYBOARD_INPUT for better compatibility
          container.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
        } else if (container.mozRequestFullScreen) {
          // Firefox
          container.mozRequestFullScreen();
        } else if (container.msRequestFullscreen) {
          // IE/Edge
          container.msRequestFullscreen();
        } else if (isMobile) {
          // Mobile fullscreen handling
          if (videoRef.current) {
            // iOS Safari - use video element fullscreen
            if (videoRef.current.webkitEnterFullscreen) {
              try {
                videoRef.current.webkitEnterFullscreen();
                return;
              } catch (error) {
                logger.warn('iOS Safari fullscreen failed', { error });
              }
            }
            
            // Android Chrome - try container fullscreen with user gesture
            if (container.webkitRequestFullscreen) {
              try {
                container.webkitRequestFullscreen();
                return;
              } catch (error) {
                logger.warn('Android Chrome fullscreen failed', { error });
              }
            }
            
            // Fallback: try to maximize the video container
            try {
              container.style.position = 'fixed';
              container.style.top = '0';
              container.style.left = '0';
              container.style.width = '100vw';
              container.style.height = '100vh';
              container.style.zIndex = '9999';
              container.style.backgroundColor = 'var(--video-background, #000)';
              setIsFullscreen(true);
            } catch (error) {
              logger.warn('Mobile CSS fullscreen failed', { error });
            }
          }
        } else {
          // Desktop fallback
          logger.warn('Fullscreen API not supported on this browser');
          alert('Fullscreen is not supported on this browser. Please try a different browser.');
        }
      } catch (error) {
        logger.warn('Fullscreen request failed', { error, isMobile });
        if (!isMobile) {
          alert('Unable to enter fullscreen mode. Please try a different browser.');
        }
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      setLoadingStage('loading-metadata');
      // On mobile, metadata is sufficient to let user press play; hide overlay
      if (isMobile) {
        setIsLoading(false);
        setLoadingStage('ready');
        clearWatchdog();
      }
    }
  };

  const clearWatchdog = () => {
    try {
      if (watchdogRef.current) {
        clearTimeout(watchdogRef.current);
        watchdogRef.current = null;
      }
    } catch (error) {
      logger.error('Error clearing watchdog', { error });
    }
  };

  const handleError = (e) => {
    setHasError(true);
    setIsLoading(false);
    clearWatchdog();
    
    // Provide more specific error messages with mobile context
    let errorMessage = 'Failed to load video';
    if (e.target.error) {
      switch (e.target.error.code) {
        case 1: // MEDIA_ERR_ABORTED
          errorMessage = isMobile ? 'Video loading was interrupted (mobile network issue)' : 'Video loading was aborted';
          break;
        case 2: // MEDIA_ERR_NETWORK
          // Check if it's a 404 by trying to fetch the URL
          if (proxyUrl && proxyUrl.includes('/portal/api/video/proxy')) {
            fetch(proxyUrl, { method: 'HEAD' })
              .then(response => {
                if (response.status === 404) {
                  setIsVideoNotFound(true);
                  setHasError(true);
                  setIsLoading(false);
                }
              })
              .catch(() => {
                // Ignore fetch errors, use default message
              });
          }
          // If we already know it's not found, don't set network error message
          if (!isVideoNotFound) {
            errorMessage = isMobile ? 'Network error - check your mobile connection' : 'Network error while loading video';
          } else {
            errorMessage = 'Video not available';
          }
          break;
        case 3: // MEDIA_ERR_DECODE
          errorMessage = isMobile ? 'Video format not supported on mobile device' : 'Video decoding error';
          break;
        case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
          errorMessage = 'Video not available';
          setIsVideoNotFound(true);
          break;
        default:
          errorMessage = isMobile ? 'Mobile video playback error' : 'Unknown video error';
      }
    }
    
    // Don't retry if video is not found (404)
    if (isVideoNotFound) {
      errorMessage = 'Video not available';
      if (onError) {
        onError({ ...e, message: errorMessage });
      }
      return;
    }
    
    // Check if it's a production video proxy issue and try fallback
    if (proxyUrl && proxyUrl.includes('/portal/api/video/proxy') && !fallbackAttempted && !isVideoNotFound) {
      setUseFallback(true);
      setFallbackAttempted(true);
      setHasError(false);
      setIsLoading(true);
      return; // Don't show error yet, try fallback first
    }
    
    if (proxyUrl && proxyUrl.includes('/portal/api/video/proxy') && fallbackAttempted) {
      errorMessage = 'Video server is not available. Please contact support.';
    }
    
    if (onError) {
      onError({ ...e, message: errorMessage });
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
  };

  const handleMouseLeave = () => {
    if (!isPlaying) return; // Keep controls visible when paused
    setShowControls(false);
  };

  const handleTouchStart = () => {
    setShowControls(true);
  };

  // Close speed menu when clicking outside
  useEffect(() => {
    if (!showSpeedMenu) return;
    function handleClickOutside(e) {
      if (
        speedMenuRef.current &&
        !speedMenuRef.current.contains(e.target) &&
        speedWrapperRef.current &&
        !speedWrapperRef.current.contains(e.target)
      ) {
        setShowSpeedMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showSpeedMenu]);

  if (hasError) {
    return (
      <ErrorMessage>
        <h4>Video Not Available</h4>
        {isVideoNotFound ? (
          <p>The requested video file could not be found on the server.</p>
        ) : (
          <>
            <p>The video could not be loaded. This may be due to:</p>
            <ul style={{ textAlign: 'left', margin: '1rem 0' }}>
              <li>Network connectivity issues</li>
              <li>SSL/connection problems</li>
              <li>Video format not supported</li>
            </ul>
          </>
        )}
        <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginTop: '1rem' }}>
          Please contact support if this issue persists.
        </p>
      </ErrorMessage>
    );
  }

  // Safety check for recording
  if (!recording) {
    return (
      <ErrorMessage>
        <h4>No Video Selected</h4>
        <p>Please select a video to play.</p>
      </ErrorMessage>
    );
  }

  try {
    return (
      <div>
        <VideoContainer 
          onMouseMove={handleMouseMove} 
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
        >
        <Video
          ref={videoRef}
          src={videoSrc}
          autoPlay={false}
          muted={isMobile}
          playsInline
          webkit-playsinline="true"
          x5-playsinline="true"
          x5-video-player-type="h5"
          x5-video-player-fullscreen="true"
          x5-video-orientation="portrait"
          controlsList="nodownload nofullscreen noremoteplayback"
          disablePictureInPicture
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onError={handleError}
          onPlay={() => {
            try {
              setIsPlaying(true);
              // Hide loading overlay when video starts playing
              setIsLoading(false);
              setLoadingStage('ready');
              clearWatchdog();
            } catch (error) {
              logger.error('Error in onPlay handler', { error });
            }
          }}
          onPause={() => {
            try {
              setIsPlaying(false);
              // Clear loading state when paused to prevent stuck loading indicators
              setIsLoading(false);
              setLoadingStage('ready');
              // Clear any active timers/polling when paused
              clearWatchdog();
              if (readinessPollRef.current) {
                clearInterval(readinessPollRef.current);
                readinessPollRef.current = null;
              }
            } catch (error) {
              logger.error('Error in onPause handler', { error });
            }
          }}
          onVolumeChange={(e) => setVolume(e.target.volume)}
          onLoadStart={() => {
            try {
              // Only show loading if video is actually playing or about to play
              // Don't show loading if video is paused/stopped
              if (isPlaying || videoRef.current?.readyState >= 3) {
                setIsLoading(true);
                setLoadingStage('initializing');
                setLoadingStartTime(Date.now());
                setTookTooLong(false);
                clearWatchdog();
                // Start readiness polling to avoid stuck spinner if events are missed
                if (readinessPollRef.current) {
                  clearInterval(readinessPollRef.current);
                  readinessPollRef.current = null;
                }
                readinessPollRef.current = setInterval(() => {
                  try {
                    const v = videoRef.current;
                    if (!v) return;
                    // If we have metadata or can play, hide overlay
                    if (v.readyState >= 1 || (v.buffered && v.buffered.length > 0)) {
                      if (isLoading && !isPlaying) {
                        setIsLoading(false);
                        setLoadingStage('ready');
                      }
                    }
                  } catch (e) {
                    // ignore
                  }
                }, 500);
                // Mobile networks are slow; surface a fallback if not ready in 5s (reduced from 10s)
                const timeout = isMobile ? 5000 : 10000;
                watchdogRef.current = setTimeout(() => {
                  try {
                    if (!isPlaying) {
                      setTookTooLong(true);
                      // Force-hide overlay so user can interact
                      setIsLoading(false);
                      setLoadingStage('ready');
                      // If using proxy on mobile, switch to direct URL automatically
                      try {
                        if (isMobile && proxyUrl && proxyUrl.includes('/portal/api/video/proxy')) {
                          setUseFallback(true);
                          setFallbackAttempted(true);
                        }
                      } catch (switchErr) {
                        logger.warn('Failed to switch to direct URL fallback', { error: switchErr });
                      }
                    }
                  } catch (error) {
                    logger.error('Error in watchdog timeout', { error });
                  }
                }, timeout);
              }
            } catch (error) {
              logger.error('Error in onLoadStart handler', { error });
            }
          }}
          onCanPlay={() => {
            try {
              // Hide loading indicator when video can play
              setIsLoading(false);
              setLoadingStage('ready');
              clearWatchdog();
              if (readinessPollRef.current) {
                clearInterval(readinessPollRef.current);
                readinessPollRef.current = null;
              }
            } catch (error) {
              logger.error('Error in onCanPlay handler', { error });
            }
          }}
          onWaiting={() => {
            try {
              // Only show loading if video is actually playing or about to play
              // Don't show loading if video is paused/stopped
              if (isPlaying || videoRef.current?.readyState >= 3) {
                setIsLoading(true);
                setLoadingStage('buffering');
              }
            } catch (error) {
              logger.error('Error in onWaiting handler', { error });
            }
          }}
          onCanPlayThrough={() => {
            try {
              // Hide loading indicator immediately when video can play through
              setIsLoading(false);
              setLoadingStage('ready');
              clearWatchdog();
              if (readinessPollRef.current) {
                clearInterval(readinessPollRef.current);
                readinessPollRef.current = null;
              }
            } catch (error) {
              logger.error('Error in onCanPlayThrough handler', { error });
            }
          }}
          onStalled={() => {
            try {
              // Only show loading if video is actually playing or about to play
              // Don't show loading if video is paused/stopped
              if (isPlaying || videoRef.current?.readyState >= 3) {
                setIsLoading(true);
                setLoadingStage('buffering');
              }
            } catch (error) {
              logger.error('Error in onStalled handler', { error });
            }
          }}
          onSuspend={() => {
            // Video loading suspended
          }}
          onProgress={(e) => {
            // Only process buffering progress if video is playing or about to play
            // This prevents unnecessary buffering when video is paused
            if (!isPlaying && videoRef.current?.paused) {
              return;
            }
            
            if (e.target.buffered.length > 0) {
              const bufferedEnd = e.target.buffered.end(e.target.buffered.length - 1);
              const duration = e.target.duration;
              if (duration > 0) {
                const progress = (bufferedEnd / duration) * 100;
                const bufferedMB = bufferedEnd / 1024 / 1024;
                const totalMB = duration / 1024 / 1024;
                
                setLoadProgress(progress);
                setBufferedMB(bufferedMB);
                setTotalMB(totalMB);
                
                // Update loading stage based on progress
                if (progress < 10) setLoadingStage('initializing');
                else if (progress < 25) setLoadingStage('loading-metadata');
                else if (progress < 50) setLoadingStage('buffering');
                else if (progress < 75) setLoadingStage('almost-ready');
                else if (progress < 95) setLoadingStage('finalizing');
                else setLoadingStage('ready');

                // Mobile: More conservative buffering approach to prevent crashes
                if (isMobile && progress >= 5 && isLoading && !isPlaying) {
                  setIsLoading(false);
                  setLoadingStage('ready');
                }
                
                // Mobile: Hide loading after 2 seconds to prevent infinite loading
                if (isMobile && isLoading && loadingStartTime && (Date.now() - loadingStartTime) > 2000 && !isPlaying) {
                  setIsLoading(false);
                  setLoadingStage('ready');
                }
                
                // Update last logged progress for internal tracking
                if (Math.floor(progress) % 5 === 0 && progress > 0 && progress !== lastLoggedProgress) {
                  setLastLoggedProgress(progress);
                }
              }
            }
          }}
          onEnded={() => {
            try {
              setIsPlaying(false);
              // Clear loading state when video ends
              setIsLoading(false);
              setLoadingStage('ready');
              // Clear any active timers/polling when video ends
              clearWatchdog();
              if (readinessPollRef.current) {
                clearInterval(readinessPollRef.current);
                readinessPollRef.current = null;
              }
            } catch (error) {
              logger.error('Error in onEnded handler', { error });
            }
          }}
          preload={isMobile ? 'auto' : 'metadata'}
          crossOrigin="anonymous"
        />
        
        {isLoading && !isPlaying && (
          <LoadingOverlay>
            <LoadingHeader>
              <LoadingSpinner />
              <span>Loading Video</span>
            </LoadingHeader>
            
            {loadProgress > 0 && (
              <ProgressContainer>
                <LoadingProgressBar>
                  <LoadingProgressFill $progress={loadProgress} />
                </LoadingProgressBar>
                <ProgressText>
                  {Math.round(loadProgress)}% loaded
                </ProgressText>
                <LoadingStage>
                  {loadingStage === 'initializing' ? 'Initializing...' : 
                   loadingStage === 'loading-metadata' ? 'Loading metadata...' :
                   loadingStage === 'buffering' ? 'Buffering video...' :
                   loadingStage === 'almost-ready' ? 'Almost ready...' :
                   loadingStage === 'finalizing' ? 'Finalizing...' : 'Ready to play!'}
                </LoadingStage>
                {totalMB > 0 && (
                  <FileSizeInfo>
                    {bufferedMB.toFixed(1)}MB / {totalMB.toFixed(1)}MB
                  </FileSizeInfo>
                )}
              </ProgressContainer>
            )}
            
            {loadProgress === 0 && (
              <InitializingText>
                {isMobile 
                  ? (loadingStage === 'initializing' ? 'Preparing video...' : 'Connecting...')
                  : (loadingStage === 'initializing' ? 'Preparing video stream...' : 'Connecting to video server...')
                }
              </InitializingText>
            )}
          </LoadingOverlay>
        )}
        
        <VideoControls $show={showControls}>
          <PlayButton onClick={handlePlayPause}>
            {isPlaying ? <FiPause /> : <FiPlay />}
          </PlayButton>
          
          <ProgressBar onClick={handleProgressClick}>
            <ProgressFill $progress={(currentTime / duration) * 100} />
          </ProgressBar>
          
          <TimeDisplay>
            {formatTime(currentTime)} / {formatTime(duration)}
          </TimeDisplay>
          
          <VolumeButton onClick={handleVolumeToggle}>
            {isMuted ? <FiVolumeX /> : <FiVolume2 />}
          </VolumeButton>
          
          <SpeedControlWrapper ref={speedWrapperRef}>
            <SpeedButton onClick={() => setShowSpeedMenu(!showSpeedMenu)}>
              {playbackSpeed}x
            </SpeedButton>
            <SpeedMenu ref={speedMenuRef} $show={showSpeedMenu}>
              <SpeedOption $active={playbackSpeed === 1} onClick={() => handleSpeedChange(1)}>1x</SpeedOption>
              <SpeedOption $active={playbackSpeed === 1.5} onClick={() => handleSpeedChange(1.5)}>1.5x</SpeedOption>
              <SpeedOption $active={playbackSpeed === 2} onClick={() => handleSpeedChange(2)}>2x</SpeedOption>
              <SpeedOption $active={playbackSpeed === 2.5} onClick={() => handleSpeedChange(2.5)}>2.5x</SpeedOption>
              <SpeedOption $active={playbackSpeed === 3} onClick={() => handleSpeedChange(3)}>3x</SpeedOption>
            </SpeedMenu>
          </SpeedControlWrapper>
          
          {fullscreenSupported && (
            <FullscreenButton onClick={handleFullscreen}>
              {isFullscreen ? <FiMinimize /> : <FiMaximize />}
            </FullscreenButton>
          )}
        </VideoControls>
      </VideoContainer>
      {tookTooLong && (
        <div style={{ textAlign: 'center', marginTop: '8px' }}>
          <button
            onClick={() => window.open(recording?.url, '_blank')}
            style={{
              padding: '10px 14px',
              backgroundColor: 'var(--info-color)',
              color: 'var(--text-inverse)',
              border: 'none',
              borderRadius: 0,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500
            }}
          >
            Open video in new tab
          </button>
        </div>
      )}
      
      <VideoInfo>
        <VideoTitle>
          {recording.week && `Week ${recording.week}: `}
          {recording.title}
        </VideoTitle>
        {recording.description && recording.description.trim() && recording.description.trim() !== '<p></p>' && (
          <p style={{ margin: '0.5rem 0', color: 'var(--text-muted)' }}>
            {recording.description}
          </p>
        )}
        <VideoMeta>
          {recording.recording_date && (
            <MetaItem>
              <FiCalendar size={16} />
              {(() => {
                try {
                  const iso = recording.recording_date;
                  const dateOnly = typeof iso === 'string' ? iso.slice(0,10) : '';
                  if (dateOnly) {
                    const parts = dateOnly.split('-');
                    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
                    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                  }
                  const dt = utcToChicago(iso);
                  return dt.toFormat('MMMM d, yyyy');
                } catch (e) {
                  return '';
                }
              })()}
            </MetaItem>
          )}
          {recording.duration && (
            <MetaItem>
              <FiClock size={16} />
              {recording.duration}
            </MetaItem>
          )}
        </VideoMeta>
      </VideoInfo>
    </div>
  );
  } catch (error) {
    logger.error('VideoPlayer render error', { error });
    return (
      <ErrorMessage>
        <h4>Video Player Error</h4>
        <p>An error occurred while rendering the video player.</p>
        <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginTop: '1rem' }}>
          Error: {error.message}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 16px',
            backgroundColor: 'var(--info-color)',
            color: 'var(--text-inverse)',
            border: 'none',
            borderRadius: 0,
            cursor: 'pointer',
            marginTop: '1rem'
          }}
        >
          Reload Page
        </button>
      </ErrorMessage>
    );
  }
}

export default VideoPlayer;
